// Operator inbox — open Jerry offer requests in the operator's delivery area +
// own-offer lifecycle tabs. Structure mirrors MyValut.jsx 1:1 (tabs in ?tab=,
// visibility refetch, 401 → logout). Data: one provider-list call per tab
// (counts ride along). 403 = the user is not a pump provider → friendly notice,
// no Sentry. Providers only ever see requests inside their geofence; the
// whole-market view lives in the betoni.online admin Jerry dashboard.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router";
import { Button, Stack, Tab, Tabs, Typography } from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { PROVIDER_TABS, normalizeCounts } from "@ibetoni/jerry-client";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import { providerApi } from "src/utils/tarjouspyynnot/providerApi";
import { captureError } from "src/utils/errorReporting";
import TilaaBackdrop from "src/components/quote/TilaaBackdrop";
import StepHeader from "src/components/quote/StepHeader";
import FrostedCard from "src/components/quote/FrostedCard";
import TarjouspyyntoListCard from "src/components/tarjouspyynnot/TarjouspyyntoListCard";
import { amberCtaSx } from "src/components/quote/amberCtaSx";

// Operators reach the quote wizard (/tilaa) only via the buried avatar menu;
// surface it here so a logged-in user can always start a new request. Also
// shown to non-providers who land on this page (they're really customers).
function NewRequestButton() {
    const t = useT();
    return (
        <Button
            component={RouterLink}
            to="/tilaa"
            variant="contained"
            startIcon={<RequestQuoteIcon />}
            sx={amberCtaSx}
        >
            {t("common.nav.requestQuoteCta")}
        </Button>
    );
}

function emptyCopy(key, t) {
    switch (key) {
        case "avoimet": return t("provider.list.emptyAvoimet");
        case "tarjotut": return t("provider.list.emptyTarjotut");
        case "voitetut": return t("provider.list.emptyVoitetut");
        case "paattyneet": return t("provider.list.emptyPaattyneet");
        default: return "";
    }
}

export default function Tarjouspyynnot() {
    const { token, logout } = useAuth();
    const t = useT();
    const [params, setParams] = useSearchParams();
    const activeKey = PROVIDER_TABS.find((tab) => tab.key === params.get("tab"))?.key || "avoimet";

    const [counts, setCounts] = useState(() => normalizeCounts());
    const [rows, setRows] = useState(null); // null = loading, [] = loaded-empty
    const [error, setError] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const lastFetchRef = useRef(0);

    const fetchData = useCallback(async ({ silent = false } = {}) => {
        lastFetchRef.current = Date.now();
        if (!silent) {
            setRows(null);
            setError(null);
            setForbidden(false); // allow re-check after a role grant
        }
        try {
            const data = await providerApi(token).listProviderRequests(activeKey);
            setCounts(normalizeCounts(data?.counts));
            setRows(Array.isArray(data?.requests) ? data.requests : []);
        } catch (err) {
            if (err.status === 401) { logout(); return; }
            if (err.status === 403) { setForbidden(true); setRows([]); return; }
            captureError(err, { tags: { entity: "pumppuRequest", operation: "provider_list" } });
            setError(err);
            setRows([]);
        }
    }, [token, activeKey, logout]);

    useEffect(() => {
        document.title = t("provider.list.pageTitle");
        return () => { document.title = t("provider.list.pageTitleFallback"); };
    }, [t]);

    useEffect(() => {
        async function run() {
            await fetchData();
        }
        run();
    }, [fetchData]);

    // Silent refetch on tab focus, debounced 5s (MyValut pattern).
    useEffect(() => {
        function onVisible() {
            if (document.visibilityState !== "visible") return;
            if (Date.now() - lastFetchRef.current < 5000) return;
            fetchData({ silent: true });
        }
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [fetchData]);

    function selectTab(_e, key) {
        const next = new URLSearchParams(params);
        next.set("tab", key);
        setParams(next, { replace: true });
    }

    if (forbidden) {
        return (
            <TilaaBackdrop>
                <StepHeader>{t("common.nav.myRequests")}</StepHeader>
                <FrostedCard sx={{ p: 2, mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {t("provider.list.providersOnlyNotice")}
                    </Typography>
                    <NewRequestButton />
                </FrostedCard>
            </TilaaBackdrop>
        );
    }

    return (
        <TilaaBackdrop>
            <Stack
                direction="row"
                sx={{ justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}
            >
                <StepHeader>{t("common.nav.myRequests")}</StepHeader>
                <NewRequestButton />
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, mb: 3 }}>
                {t("provider.list.intro")}
            </Typography>

            <Tabs
                value={activeKey}
                onChange={selectTab}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{ mb: 3 }}
            >
                {PROVIDER_TABS.map((tab) => (
                    <Tab key={tab.key} value={tab.key} label={`${tab.label} (${counts[tab.key] ?? 0})`} />
                ))}
            </Tabs>

            <Stack spacing={2}>
                {rows === null && <Typography variant="body2">{t("common.loading")}</Typography>}
                {rows !== null && rows.length === 0 && !error && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {emptyCopy(activeKey, t)}
                    </Typography>
                )}
                {error && (
                    <Typography variant="body2" color="error">
                        {t("provider.list.loadFailed")}
                    </Typography>
                )}
                {rows && rows.map((row) => (
                    <TarjouspyyntoListCard key={row.pumppuRequestId} row={row} />
                ))}
            </Stack>
        </TilaaBackdrop>
    );
}
